import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import api from "../api/api";
import Breadcrumb from "../components/Breadcrumb";
import { API_BASE_URL } from "../config";
import "../styles/components/ProductDetails.scss";

function ProductDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart, openCart } = useCart();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState("");
  const [selectedColorId, setSelectedColorId] = useState(null);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const res = await api.get(`/product/${id}`);
        const productData = res.data.results;

        setProduct(productData);

        const allImages = [
          productData.image,
          ...(productData.images?.map((img) => img.image) || []),
        ].filter(Boolean);

        if (allImages.length > 0) {
          setSelectedImage(allImages[0]);
        }

        // A product with exactly one color option isn't really a "choice" -
        // preselect it (as long as it's actually available) so the customer
        // isn't blocked on clicking something with no real alternative.
        // 2+ colors are a genuine choice and start unselected on purpose.
        const availableColors = (productData.colors || []).filter((c) => c.is_available);
        if (productData.colors?.length === 1 && availableColors.length === 1) {
          setSelectedColorId(availableColors[0].id);
        } else {
          setSelectedColorId(null);
        }
      } catch (err) {
        console.error(err);
        setProduct(null);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  const formatPrice = (value) => {
    const number = Number(value || 0);
    return number.toFixed(2);
  };

  const allImages = useMemo(() => {
    if (!product) return [];

    return [
      product.image,
      ...(product.images?.map((img) => img.image) || []),
    ].filter(Boolean);
  }, [product]);

  const colors = useMemo(() => product?.colors || [], [product]);

  const selectedColor = useMemo(
    () => colors.find((c) => c.id === selectedColorId) || null,
    [colors, selectedColorId]
  );

  // A color must be picked whenever there's a real choice to make (2+
  // options) - a single option is auto-selected on load, and no options at
  // all means this product doesn't use color variants, so neither case
  // should ever block the buttons.
  const colorSelectionRequired = colors.length > 1;
  const canOrder = !colorSelectionRequired || !!selectedColor;

  const breadcrumbItems = useMemo(() => {
    if (!product) return [];

    const items = [{ label: "Home", to: "/" }];
    const category = product.category || null;
    const parent = category?.parent || null;

    if (parent) {
      items.push({ label: parent.name, to: `/c/${parent.id}` });
    }

    if (category) {
      items.push({
        label: category.name,
        to: parent
          ? `/c/${parent.id}/${category.id}`
          : `/c/${category.id}`,
      });
    }

    items.push({ label: product.name });

    return items;
  }, [product]);

  if (loading) return <p className="pd-state">Loading...</p>;
  if (!product) return <p className="pd-state">Product not found</p>;

  const mainImageUrl = selectedImage
    ? `${API_BASE_URL}/images/products/${selectedImage}`
    : `${API_BASE_URL}/images/products/default.jpg`;

  const hasDiscount =
    Number(product.discount_percentage) > 0 &&
    Number(product.final_price) > 0 &&
    Number(product.final_price) < Number(product.original_price);

  const handleAddToCart = () => {
    if (!canOrder) return;
    addToCart(product, 1, selectedColor);
    openCart();
  };

  const handleBuyNow = () => {
    if (!canOrder) return;
    addToCart(product, 1, selectedColor);
    navigate("/checkout");
  };

  return (
    <div className="pd">
      <div className="page-head">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="pd-grid">
        <div className="pd-media">
          <div className="pd-image">
            <img src={mainImageUrl} alt={product.name} fetchpriority="high" />
          </div>

          {allImages.length > 1 && (
            <div className="pd-thumbs">
              {allImages.map((image, index) => {
                const thumbUrl = `${API_BASE_URL}/images/products/${image}`;
                const isActive = selectedImage === image;

                return (
                  <button
                    type="button"
                    key={`${image}-${index}`}
                    className={`pd-thumb ${isActive ? "active" : ""}`}
                    onClick={() => setSelectedImage(image)}
                  >
                    <img
                      src={thumbUrl}
                      alt={`${product.name} ${index + 1}`}
                      loading="lazy"
                      decoding="async"
                    />
                  </button>
                );
              })}
            </div>
          )}

          <div className="pd-note">
            {product.customized ? "Customizable" : "Ready-made"}
          </div>
        </div>

        <div className="pd-info">
          <h1 className="page-title pd-title">{product.name}</h1>

          <div className="pd-price-row">
            <div className="pd-price-box">
              {hasDiscount ? (
                <>
                  <div className="pd-price-new">
                    ${formatPrice(product.final_price)}
                  </div>
                  <div className="pd-price-old">
                    ${formatPrice(product.original_price)}
                  </div>
                  <div className="pd-discount-badge">
                    -{Number(product.discount_percentage).toFixed(0)}%
                  </div>
                </>
              ) : (
                <div className="pd-price">
                  ${formatPrice(product.original_price)}
                </div>
              )}
            </div>

            <div className="pd-chip">
              {product.customized ? "Customizable" : "Standard"}
            </div>
          </div>

          {colors.length > 0 && (
            <div className="pd-colors">
              <h3>
                {colorSelectionRequired ? "Select a Color" : "Color"}
              </h3>
              <div className="pd-color-swatches">
                {colors.map((color) => {
                  const isSelected = selectedColorId === color.id;
                  const isUnavailable = !color.is_available;

                  return (
                    <button
                      type="button"
                      key={color.id}
                      className={`pd-color-swatch ${isSelected ? "selected" : ""} ${
                        isUnavailable ? "unavailable" : ""
                      }`}
                      style={{ backgroundColor: color.hex_value }}
                      title={isUnavailable ? `${color.name} (currently unavailable)` : color.name}
                      disabled={isUnavailable}
                      onClick={() => setSelectedColorId(color.id)}
                    >
                      {isSelected && <span className="pd-color-check">✓</span>}
                    </button>
                  );
                })}
              </div>
              <div className="pd-color-label">
                {selectedColor
                  ? selectedColor.name
                  : colorSelectionRequired
                  ? "Please select a color"
                  : ""}
              </div>
            </div>
          )}

          <div className="pd-actions">
            <button
              className="pd-btn primary"
              onClick={handleAddToCart}
              disabled={!canOrder}
              title={!canOrder ? "Please select a color first" : undefined}
            >
              Add to Cart
            </button>

            <button
              className="pd-btn ghost"
              onClick={handleBuyNow}
              disabled={!canOrder}
              title={!canOrder ? "Please select a color first" : undefined}
            >
              Buy Now
            </button>
          </div>

          <div className="pd-specs">
            <h3>Details</h3>

            <div className="pd-spec-grid">
              <div className="spec">
                <span className="k">Category</span>
                <span className="v">{product.category?.name || "—"}</span>
              </div>

              <div className="spec">
                <span className="k">Customized</span>
                <span className="v">{product.customized ? "Yes" : "No"}</span>
              </div>

              <div className="spec">
                <span className="k">Discount</span>
                <span className="v">
                  {Number(product.discount_percentage) > 0
                    ? `${Number(product.discount_percentage).toFixed(0)}%`
                    : "No discount"}
                </span>
              </div>
            </div>
          </div>

          <div className="pd-divider" />

          <div className="pd-long">
            <h3>Description</h3>
            <p>{product.description || "—"}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductDetails;